import { isShareView } from "../share/shareView";
import storage from "./storage";

/*
 * KEEP PLAYBACK ACROSS A REFRESH. The place you were in (which day, how far through it, what speed, whether it ran in 1:1) is
 * written to sessionStorage while playback is open, and read back once when the dashboard loads, which then re-enters playback
 * and seeks to it (paused). sessionStorage is per tab and dies with the tab, so it can never carry a stale place into a new
 * visit. Leaving playback keeps it as an AWAY place (below). Every read and write is guarded: a blocked or full store just means no resume.
 */
const KEY = "skydive.playbackResume";
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

/*
 * THE SAME SAVED PLACE ALSO SERVES "LEAVE PLAYBACK AND COME BACK" (2026-10-11, Chris: a user who flips between Live and playback
 * wants to come back to playback where they were). One record, one flag:
 *
 *   away absent  -> playback is OPEN: a refresh re-enters it (loadPlaybackResume)
 *   away: true   -> the user LEFT playback on purpose: a refresh must NOT re-enter it, but the header toggle resumes it (loadAwayPlace)
 *
 * The flag is the whole difference. Without it, simply not clearing the record on exit would drag a refreshed Live view back into playback.
 * Each reader returns only its own kind, so neither can see the other's record.
 *
 * Every record carries the site it was saved for. A place is a position inside ONE site's day, so it means nothing on another site, and
 * the site is a per-tab-switchable global (the same trap as every other per-site value in this app).
 */
export interface PlaybackResume { date: string; virtualTimeMs: number; realOffsetMs: number; realTimeMode: boolean; speed: number; at: number; siteId: string | null; away: boolean }

const currentSiteId = (): string | null => {
  try {
    const id = storage.get("siteId");
    return id ? String(id) : null;
  } catch (e) { return null; }
};

export const savePlaybackResume = (r: Omit<PlaybackResume, "at" | "siteId" | "away"> & { away?: boolean }) => {
  if (isShareView()) return;   // a recipient's viewer must never overwrite the owner's saved place (same browser, same storage)
  try { sessionStorage.setItem(KEY, JSON.stringify({ ...r, away: r.away === true, siteId: currentSiteId(), at: Date.now() })); } catch (e) { /* no resume, nothing else lost */ }
};
// The user is leaving playback on purpose: keep the place, but mark it so a refresh does not re-enter playback.
export const markPlaybackAway = (r: Omit<PlaybackResume, "at" | "siteId" | "away">) => savePlaybackResume({ ...r, away: true });
export const clearPlaybackResume = () => {
  if (isShareView()) return;
  try { sessionStorage.removeItem(KEY); } catch (e) { /* ignore */ }
};

const readRecord = (): PlaybackResume | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const r = JSON.parse(raw);
    if (!r || typeof r.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(r.date) || !(Date.now() - Number(r.at) < MAX_AGE_MS)) return null;
    // A record saved for another site is not this site's place. (A record with no site, from before sites were recorded, still counts.)
    const savedSite = typeof r.siteId === "string" && r.siteId ? r.siteId : null;
    if (savedSite !== null && savedSite !== currentSiteId()) return null;
    return {
      date: r.date, virtualTimeMs: Math.max(0, Number(r.virtualTimeMs) || 0), realOffsetMs: Math.max(0, Number(r.realOffsetMs) || 0),
      realTimeMode: r.realTimeMode === true, speed: Number(r.speed) || 1, at: Number(r.at), siteId: savedSite, away: r.away === true,
    };
  } catch (e) { return null; }
};

// Playback is OPEN: what a refresh restores.
export const loadPlaybackResume = (): PlaybackResume | null => {
  if (isShareView()) return null;
  const r = readRecord();
  return r && !r.away ? r : null;
};
// The user LEFT playback on purpose: what the header toggle resumes. Never what a refresh restores.
export const loadAwayPlace = (): PlaybackResume | null => {
  if (isShareView()) return null;
  const r = readRecord();
  return r && r.away ? r : null;
};

/* The zoom (a page's zoomed view and its sub-view) is kept the same way, in its own key, in live and in playback. */
const ZKEY = "skydive.zoomResume";
export interface ZoomResume { slotIndex: number; pageName: string; dimension: string | null; at: number }
export const saveZoomResume = (z: Omit<ZoomResume, "at">) => { try { sessionStorage.setItem(ZKEY, JSON.stringify({ ...z, at: Date.now() })); } catch (e) { /* ignore */ } };
export const clearZoomResume = () => { try { sessionStorage.removeItem(ZKEY); } catch (e) { /* ignore */ } };
export const loadZoomResume = (): ZoomResume | null => {
  try {
    const raw = sessionStorage.getItem(ZKEY);
    if (!raw) return null;
    const z = JSON.parse(raw);
    if (!z || !Number.isInteger(z.slotIndex) || typeof z.pageName !== "string" || !(Date.now() - Number(z.at) < MAX_AGE_MS)) return null;
    return { slotIndex: z.slotIndex, pageName: z.pageName, dimension: typeof z.dimension === "string" ? z.dimension : null, at: Number(z.at) };
  } catch (e) { return null; }
};
